import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEye, FiCalendar, FiDollarSign, FiSearch, FiRefreshCw, FiBriefcase, FiMapPin, FiAward, FiX } from 'react-icons/fi';
import { getPublicJobs, previewJobMatch } from '../../api/client';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Select,
  MenuItem
} from '@mui/material';

function PublicJobs({ adminToken, onLogout }) {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);

  // Advanced Filter States
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedSalary, setSelectedSalary] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');
  const [selectedRemote, setSelectedRemote] = useState('');

  const defaultDepartments = [
    "Software Engineering",
    "Data Science",
    "Product Management",
    "Marketing",
    "Sales",
    "Human Resources",
    "Finance",
    "Operations",
    "IT"
  ];

  const availableDepartments = Array.from(
    new Set([
      ...defaultDepartments,
      ...jobs.map((job) => job.department_name).filter(Boolean)
    ])
  ).sort();

  const experienceOptions = [
    { value: '', label: t('publicJobDescriptions.allExperiences', 'All Experiences') },
    { value: 'entry', label: t('publicJobDescriptions.entryLevel', 'Entry Level (0-1 yrs)') },
    { value: 'junior', label: t('publicJobDescriptions.juniorLevel', 'Junior (1-3 yrs)') },
    { value: 'mid', label: t('publicJobDescriptions.midLevel', 'Mid Level (3-5 yrs)') },
    { value: 'senior', label: t('publicJobDescriptions.seniorLevel', 'Senior (5-8 yrs)') },
    { value: 'lead', label: t('publicJobDescriptions.leadLevel', 'Lead/Manager (8-15 yrs)') },
    { value: 'fifteen_plus', label: t('publicJobDescriptions.fifteenPlus', 'Director (15-20 yrs)') },
    { value: 'twenty_plus', label: t('publicJobDescriptions.twentyPlus', 'VP/CXO (20+ yrs)') },
    { value: 'executive', label: t('publicJobDescriptions.executive', 'Executive') }
  ];

  const salaryOptions = [
    { value: '', label: t('publicJobDescriptions.allSalaries', 'All Salaries') },
    { value: 'under_5l', label: t('publicJobDescriptions.under5l', 'Under ₹5L') },
    { value: '5l_10l', label: t('publicJobDescriptions.fiveToTen', '₹5L - ₹10L') },
    { value: '10l_15l', label: t('publicJobDescriptions.tenToFifteen', '₹10L - ₹15L') },
    { value: '15l_20l', label: t('publicJobDescriptions.fifteenToTwenty', '₹15L - ₹20L') },
    { value: '20l_25l', label: t('publicJobDescriptions.twentyToTwentyFive', '₹20L - ₹25L') },
    { value: '25l_30l', label: t('publicJobDescriptions.twentyFiveToThirty', '₹25L - ₹30L') },
    { value: '30l_50l', label: t('publicJobDescriptions.thirtyToFifty', '₹30L - ₹50L') },
    { value: '50l_70l', label: t('publicJobDescriptions.fiftyToSeventy', '₹50L - ₹70L') },
    { value: '70l_1cr', label: t('publicJobDescriptions.seventyToOneCr', '₹70L - ₹1Cr') },
    { value: '1cr_plus', label: t('publicJobDescriptions.oneCrPlus', '₹1Cr+') }
  ];

  const hasActiveFilters =
    selectedDepartment !== '' ||
    locationSearch !== '' ||
    selectedSalary !== '' ||
    selectedExperience !== '' ||
    selectedRemote !== '' ||
    searchTerm !== '';

  const handleClearFilters = () => {
    setSelectedDepartment('');
    setLocationSearch('');
    setSelectedSalary('');
    setSelectedExperience('');
    setSelectedRemote('');
    setSearchTerm('');
  };




  useEffect(() => {
    fetchPublicJobs();
  }, []);

  const fetchPublicJobs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getPublicJobs();
      setJobs(data.public_jobs || []);
    } catch (err) {
      console.error('Error fetching public jobs:', err);
      setError('Failed to load public jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewMatch = async (job) => {
    try {
      setPreviewLoading(true);
      setSelectedJob(job);
      const matchData = await previewJobMatch(job.id);
      setMatchData(matchData);
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error('Error previewing job match:', err);
      setError('Failed to preview job match. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredJobs = jobs.filter(job => {
    // Exclude jobs that have already been applied to
    if (job.user_application_status) {
      return false;
    }

    // Text search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch =
        (job.title && job.title.toLowerCase().includes(lowerSearch)) ||
        (job.company_name && job.company_name.toLowerCase().includes(lowerSearch)) ||
        (job.department_name && job.department_name.toLowerCase().includes(lowerSearch));
      if (!matchesSearch) return false;
    }

    // Department / Role Type filter
    if (selectedDepartment) {
      if (job.department_name !== selectedDepartment) return false;
    }

    // Location filter
    if (locationSearch) {
      const lowerLoc = locationSearch.toLowerCase();
      const jobLoc = (job.location || '').toLowerCase();
      const matchesLoc = jobLoc.includes(lowerLoc) ||
        (job.title && job.title.toLowerCase().includes(lowerLoc)) ||
        (job.description && job.description.toLowerCase().includes(lowerLoc));
      if (!matchesLoc) return false;
    }

    // Experience Level filter
    if (selectedExperience) {
      const jobExp = job.experience_level;
      if (jobExp) {
        if (jobExp !== selectedExperience) return false;
      } else {
        const text = `${job.title} ${job.description || ''}`.toLowerCase();
        let matchesExp = false;
        if (selectedExperience === 'entry') {
          matchesExp = text.includes('entry') || text.includes('intern') || text.includes('fresh') || text.includes('0-1 year') || text.includes('0 to 1') || text.includes('0+') || text.includes('no experience');
        } else if (selectedExperience === 'junior') {
          matchesExp = text.includes('junior') || text.includes('1-3 year') || text.includes('1 to 3') || text.includes('1+') || text.includes('2+') || text.includes('3+');
        } else if (selectedExperience === 'mid') {
          matchesExp = text.includes('mid') || text.includes('3-5 year') || text.includes('3 to 5') || text.includes('3+') || text.includes('4+') || text.includes('5+');
        } else if (selectedExperience === 'senior') {
          matchesExp = text.includes('senior') || text.includes('5-8 year') || text.includes('5 to 8') || text.includes('5+') || text.includes('6+') || text.includes('7+') || text.includes('8+');
        } else if (selectedExperience === 'lead') {
          matchesExp = text.includes('lead') || text.includes('architect') || text.includes('8+') || text.includes('9+') || text.includes('10+') || text.includes('8-10 year') || text.includes('8 to 10');
        } else if (selectedExperience === 'fifteen_plus') {
          matchesExp = text.includes('15+') || text.includes('15 year') || text.includes('15+ year') || text.includes('16+') || text.includes('17+') || text.includes('18+') || text.includes('19+') || text.includes('20+');
        } else if (selectedExperience === 'twenty_plus') {
          matchesExp = text.includes('20+') || text.includes('20 year') || text.includes('20+ year') || text.includes('25+');
        } else if (selectedExperience === 'executive') {
          matchesExp = text.includes('executive') || text.includes('director') || text.includes('vp') || text.includes('chief') || text.includes('10+ year') || text.includes('10 to');
        }
        if (!matchesExp) return false;
      }
    }

    // Salary Range filter (scale-detection & description parsing fallback)
    if (selectedSalary) {
      const min = job.salary_range_min;
      const max = job.salary_range_max;
      
      if (min !== undefined && min !== null) {
        const limitMin = min > 150000 ? 500000 : 50000;
        if (selectedSalary === 'under_5l') {
          if (min >= limitMin) return false;
        } else if (selectedSalary === '5l_10l') {
          if (min < limitMin || min > (limitMin * 2)) return false;
        } else if (selectedSalary === '10l_15l') {
          if (min < (limitMin * 2) || min > (limitMin * 3)) return false;
        } else if (selectedSalary === '15l_20l') {
          if (min < (limitMin * 3) || min > (limitMin * 4)) return false;
        } else if (selectedSalary === '20l_25l') {
          if (min < (limitMin * 4) || min > (limitMin * 5)) return false;
        } else if (selectedSalary === '25l_30l') {
          if (min < (limitMin * 5) || min > (limitMin * 6)) return false;
        } else if (selectedSalary === '30l_50l') {
          if (min < (limitMin * 6) || min > (limitMin * 10)) return false;
        } else if (selectedSalary === '50l_70l') {
          if (min < (limitMin * 10) || min > (limitMin * 14)) return false;
        } else if (selectedSalary === '70l_1cr') {
          if (min < (limitMin * 14) || min > (limitMin * 20)) return false;
        } else if (selectedSalary === '1cr_plus') {
          if (min < (limitMin * 20) && (!max || max < (limitMin * 20))) return false;
        } else if (selectedSalary === '15l_plus') {
          if (min < (limitMin * 3) && (!max || max < (limitMin * 3))) return false;
        }
      } else {
        const text = (job.description || '').toLowerCase();
        const match = text.match(/(\d+(?:\.\d+)?)\s*(?:-|to)?\s*(\d+(?:\.\d+)?)?\s*(?:lpa|lakh|l|lac|cr|crore)/);
        if (match) {
          const isCrore = /cr|crore/.test(match[0]);
          const val1 = parseFloat(match[1]) * (isCrore ? 100 : 1);
          const val2 = match[2] ? parseFloat(match[2]) * (isCrore ? 100 : 1) : val1;
          const val = (val1 + val2) / 2;
          
          if (selectedSalary === 'under_5l') {
            if (val >= 5) return false;
          } else if (selectedSalary === '5l_10l') {
            if (val < 5 || val > 10) return false;
          } else if (selectedSalary === '10l_15l') {
            if (val < 10 || val > 15) return false;
          } else if (selectedSalary === '15l_20l') {
            if (val < 15 || val > 20) return false;
          } else if (selectedSalary === '20l_25l') {
            if (val < 20 || val > 25) return false;
          } else if (selectedSalary === '25l_30l') {
            if (val < 25 || val > 30) return false;
          } else if (selectedSalary === '30l_50l') {
            if (val < 30 || val > 50) return false;
          } else if (selectedSalary === '50l_70l') {
            if (val < 50 || val > 70) return false;
          } else if (selectedSalary === '70l_1cr') {
            if (val < 70 || val > 100) return false;
          } else if (selectedSalary === '1cr_plus') {
            if (val < 100) return false;
          } else if (selectedSalary === '15l_plus') {
            if (val < 15) return false;
          }
        }
      }
    }

    // Remote Preference / Work Type filter
    if (selectedRemote) {
      const jobWorkType = job.work_type;
      if (jobWorkType) {
        const workType = jobWorkType.toLowerCase().replace('-', '_');
        if (workType !== selectedRemote) return false;
      } else {
        const text = `${job.title} ${job.department_name} ${job.description || ''}`.toLowerCase();
        let matchesRemote = false;
        if (selectedRemote === 'remote') {
          matchesRemote = text.includes('remote');
        } else if (selectedRemote === 'hybrid') {
          matchesRemote = text.includes('hybrid');
        } else if (selectedRemote === 'on_site') {
          matchesRemote = text.includes('on-site') || text.includes('onsite') || text.includes('in-office') || text.includes('on site') || text.includes('in office') || (!text.includes('remote') && !text.includes('hybrid'));
        }
        if (!matchesRemote) return false;
      }
    }

    return true;
  });

  const getApplicationStatusColor = (status) => {
    switch (status) {
      case 'applied':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Box sx={{ width: '100%', padding: '2rem' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('publicJobDescriptions.title', 'Public Job Opportunities 🚀')}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {t('publicJobDescriptions.subtitle', "Discover exciting career opportunities available for application. Jobs you've already applied to are not shown here.")}
          </Typography>

          {/* Search and Filter Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder={t('publicJobDescriptions.searchPlaceholder', 'Search jobs, companies, or departments...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: '300px' }}
            />
            <Button
              variant="outlined"
              startIcon={<FiRefreshCw />}
              onClick={fetchPublicJobs}
              disabled={loading}
            >
              {t('publicJobDescriptions.refresh', 'Refresh')}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<FiX />}
                onClick={handleClearFilters}
              >
                {t('publicJobDescriptions.clearFilters', 'Clear Filters')}
              </Button>
            )}
          </Box>

          {/* Advanced Filters Panel */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              backgroundColor: '#f8fafc',
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              mb: 4,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Department (Role Type) Filter */}
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                {t('publicJobDescriptions.roleType', 'Role Type')}
              </Typography>
              <Select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                displayEmpty
                fullWidth
                variant="outlined"
                startAdornment={
                  <InputAdornment position="start" sx={{ color: '#6366f1' }}>
                    <FiBriefcase />
                  </InputAdornment>
                }
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  height: '56px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                  },
                }}
              >
                <MenuItem value="">{t('publicJobDescriptions.departments', 'Departments')}</MenuItem>
                {availableDepartments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Location Filter */}
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                {t('publicJobDescriptions.location', 'Location')}
              </Typography>
              <TextField
                placeholder={t('publicJobDescriptions.locationPlaceholder', 'e.g. Bengaluru, Karnataka')}
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ color: '#6366f1' }}>
                      <FiMapPin />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    height: '56px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e1',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                }}
              />
            </Box>

            {/* Experience Level Filter */}
            <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                {t('publicJobDescriptions.experienceLevel', 'Experience Level')}
              </Typography>
              <Select
                value={selectedExperience}
                onChange={(e) => setSelectedExperience(e.target.value)}
                displayEmpty
                fullWidth
                variant="outlined"
                startAdornment={
                  <InputAdornment position="start" sx={{ color: '#6366f1' }}>
                    <FiAward />
                  </InputAdornment>
                }
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  height: '56px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                  },
                }}
              >
                {experienceOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Salary Range Filter */}
            <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                {t('publicJobDescriptions.salaryRange', 'Salary Range')}
              </Typography>
              <Select
                value={selectedSalary}
                onChange={(e) => setSelectedSalary(e.target.value)}
                displayEmpty
                fullWidth
                variant="outlined"
                startAdornment={
                  <InputAdornment position="start" sx={{ color: '#6366f1', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    ₹
                  </InputAdornment>
                }
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  height: '56px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                  },
                }}
              >
                {salaryOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Remote Preference Filter */}
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                {t('publicJobDescriptions.remotePreference', 'Remote Preference')}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  backgroundColor: '#f1f5f9',
                  padding: '4px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  width: '100%',
                  height: '56px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {[
                  { value: 'remote', label: t('publicJobDescriptions.remote', 'Remote') },
                  { value: 'on_site', label: t('publicJobDescriptions.onSite', 'On-site') },
                  { value: 'hybrid', label: t('publicJobDescriptions.hybrid', 'Hybrid') },
                ].map((option) => {
                  const isSelected = selectedRemote === option.value;
                  return (
                    <Button
                      key={option.value}
                      onClick={() => setSelectedRemote(selectedRemote === option.value ? '' : option.value)}
                      sx={{
                        flex: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        py: '8px',
                        height: '100%',
                        color: isSelected ? 'white' : '#475569',
                        backgroundColor: isSelected ? '#4f46e5' : 'transparent',
                        boxShadow: isSelected ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)' : 'none',
                        '&:hover': {
                          backgroundColor: isSelected ? '#4338ca' : '#e2e8f0',
                          color: isSelected ? 'white' : '#1e293b',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : filteredJobs.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {searchTerm ? 'No jobs found matching your search' : 'No public jobs available'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm ? 'Try adjusting your search terms' : 'Check back later for new opportunities'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filteredJobs.map((job) => (
              <Grid item xs={12} md={6} lg={4} key={job.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {job.title}
                      </Typography>
                      <Typography variant="subtitle1" color="primary" fontWeight={500}>
                        {job.company_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {job.department_name} • {job.location || 'Remote'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        dangerouslySetInnerHTML={{ __html: job.description }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip
                        icon={<FiDollarSign size={14} />}
                        label={`${job.required_credits} credits`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        icon={<FiCalendar size={14} />}
                        label={formatDate(job.created_at)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    {job.user_application_status && (
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={`Status: ${job.user_application_status}`}
                          color={getApplicationStatusColor(job.user_application_status)}
                          size="small"
                        />
                      </Box>
                    )}
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<FiEye />}
                      onClick={() => handlePreviewMatch(job)}
                      disabled={previewLoading}
                    >
                      {previewLoading && selectedJob?.id === job.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Preview Match'
                      )}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Preview Match Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={600}>
              Skill Match Preview
            </Typography>
            {selectedJob && (
              <Typography variant="subtitle2" color="textSecondary">
                {selectedJob.title} at {selectedJob.company_name} ({selectedJob.location || 'Remote'})
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {matchData ? (
              <Box>
                <Typography variant="body1" paragraph>
                  Here's how your skills match with this position:
                </Typography>
                {/* Add match data visualization here based on API response */}
                <pre>{JSON.stringify(matchData, null, 2)}</pre>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            {matchData && (
              <Button variant="contained" color="primary">
                Apply Now
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default PublicJobs;